// Infrastructure — §9's seventh section. Founder/admin.
//
// "RunPod/GPU + actual infra; databases, services, storage, deployment status,
// infrastructure health. *Absorbs the health/readiness widgets.*"
//
// PRESERVES, from the v1 `/settings` page:
//   Cost Alert Threshold        GET/PUT /api/cost-alert/threshold
//   Claude API Hard Cap         GET/PUT /api/circuit-breaker/hard-cap
// and from the widget §3.10 names:
//   IntegrationHealthWidget     GET     /api/integrations/health
//   Database health (by BU)     GET     /api/bu/health — business-unit grain,
//                                labeled as such (not full per-database detail)
//
// WHY THE TWO COST CONTROLS ARE HERE RATHER THAN UNDER MODELS
// ===========================================================
// §9 lists "usage/cost controls" in the Models clause, and the Founder's ruling
// is that these three working endpoints belong wherever §9 puts them but not
// inside a section that is otherwise an unbuilt placeholder. Infrastructure is
// the coherent home: it already absorbs the health and readiness widgets, A1's
// proposed home for the Tiny Tim cost tile and tool history is this section,
// and a hard cap that halts spending is an operational control rather than a
// model-selection one. The Models section links here for that clause so the
// §9 reader is not left looking for it.
//
// A1 — DIAGNOSTICS
// ================
// `MIGRATION_MAP.md` sends the Tiny Tim cost tile and tool history to
// "Operations/Settings diagnostics" and neither §7 nor §9 names a diagnostics
// surface. The shell's Settings stub proposed Settings → Infrastructure and
// that reading is carried through here rather than re-litigated. What exists
// tonight is API usage and circuit-breaker state; the Tiny Tim tile itself is
// another lane's to relocate.
import React, { useEffect, useState } from "react";

import { ENDPOINTS, put } from "../api";
import { Block, Button, Gate, Row, StatusDot, Table, Value } from "../ui";
import useResource from "../useResource";

/**
 * A numeric control that already has a value on the server.
 *
 * Written once because the two cost controls behave identically and differ only
 * in their endpoint, their key and their unit — and because a save that
 * silently does nothing is the failure mode worth spending a component to
 * avoid. The field reports what happened every time.
 */
function NumberSetting({ label, hint, endpoint, field, unit, min }) {
  const resource = useResource(endpoint);
  const [draft, setDraft] = useState("");
  const [state, setState] = useState("idle");

  useEffect(() => {
    if (resource.status === "ok" && resource.data) {
      const v = resource.data[field];
      if (v !== undefined && v !== null) setDraft(String(v));
    }
  }, [resource.status, resource.data, field]);

  async function save() {
    const value = Number(draft);
    if (!Number.isFinite(value) || (min !== undefined && value < min)) {
      setState("invalid");
      return;
    }
    setState("saving");
    const res = await put(endpoint, { [field]: value });
    setState(res.status === "ok" ? "saved" : "failed");
    if (res.status === "ok") resource.reload();
  }

  return (
    <Row label={label} hint={hint}>
      <Gate resource={resource}>
        {() => (
          <span className="set-inline">
            <span className="set-unit dim">{unit}</span>
            <input
              className="set-input mono"
              type="number"
              value={draft}
              min={min}
              aria-label={label}
              onChange={(e) => {
                setDraft(e.target.value);
                setState("idle");
              }}
            />
            <Button onClick={save} disabled={state === "saving"}>
              {state === "saving" ? "Saving…" : "Save"}
            </Button>
            {state === "saved" ? (
              <span className="dim" role="status">
                Saved.
              </span>
            ) : null}
            {state === "invalid" ? (
              <span className="dim" role="alert">
                Enter a number{min !== undefined ? ` of at least ${min}` : ""}.
              </span>
            ) : null}
            {state === "failed" ? (
              <span className="dim" role="alert">
                Could not save.
              </span>
            ) : null}
          </span>
        )}
      </Gate>
    </Row>
  );
}

function IntegrationHealth() {
  const health = useResource(ENDPOINTS.integrationsHealth, {
    isEmpty: (d) => !(d?.integrations || []).length,
  });

  return (
    <Block
      title="Integration health"
      note="The live external integrations, and when each last succeeded."
      actions={
        <Button onClick={health.reload} kind="quiet">
          Refresh
        </Button>
      }
    >
      <Gate resource={health} empty="No integrations are reporting health.">
        {(d) => (
          <Table head={["Service", "Status", "Detail", "Last sync"]}>
            {(d.integrations || []).map((i) => (
              <tr key={i.service}>
                <td>{i.service}</td>
                <td>
                  <StatusDot tone={i.status} />
                </td>
                <td className="dim">{i.error || i.details || "—"}</td>
                <td className="mono dim">{i.last_sync || "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

/**
 * Database health (by business unit) — `/api/bu/health` reports a per-BU
 * ping/primary-index/latency probe against each Motor database
 * (pmos_production + abh_production). This is coarser than true per-database
 * diagnostics (no connection-pool, replication-lag or disk figures), so it is
 * labeled as business-unit-level health rather than claiming full database
 * detail (R6).
 */
function DatabaseHealth() {
  const health = useResource(ENDPOINTS.buHealth, {
    isEmpty: (d) => !(d?.business_units || []).length,
  });

  return (
    <Block
      title="Database health (by business unit)"
      note="Per-business-unit ping/index/latency probe against each database. Not full database diagnostics — no connection-pool, replication or disk detail."
      actions={
        <Button onClick={health.reload} kind="quiet">
          Refresh
        </Button>
      }
    >
      <Gate resource={health} empty="No business units are reporting database health.">
        {(d) => (
          <Table head={["Business unit", "Status", "Latency", "Detail"]}>
            {(d.business_units || []).map((b) => (
              <tr key={b.bu}>
                <td>{b.label || b.bu}</td>
                <td>
                  <StatusDot tone={b.status} />
                </td>
                <td className="mono dim">{b.latency_ms != null ? `${b.latency_ms}ms` : "—"}</td>
                <td className="dim">{b.error || (b.ping && b.primary_index ? "—" : "index unreachable")}</td>
              </tr>
            ))}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

function SchedulerHealth() {
  const health = useResource(ENDPOINTS.schedulersHealth);

  return (
    <Block title="Schedulers" note="Every background job, and when it next runs.">
      <Gate resource={health} empty="No scheduled jobs are registered.">
        {(d) => {
          const jobs = d.jobs || d.scheduled_jobs || (Array.isArray(d) ? d : []);
          return (
            <Table head={["Job", "Scheduler", "Next run"]} empty="No scheduled jobs are registered.">
              {jobs.map((j) => (
                <tr key={j.job_id || j.name}>
                  <td>{j.name || j.job_id}</td>
                  <td className="mono dim">{j.scheduler || j.scheduler_label || "—"}</td>
                  <td className="mono dim">{j.next_run_time || "—"}</td>
                </tr>
              ))}
            </Table>
          );
        }}
      </Gate>
    </Block>
  );
}

function CostControls() {
  const usage = useResource(ENDPOINTS.apiUsageSummary);
  const breaker = useResource(ENDPOINTS.circuitBreakerStatus);

  return (
    <>
      <Block
        title="Spend controls"
        note="Preserved from the v1 Settings page. Both write straight through to the server."
      >
        <NumberSetting
          label="Cost alert threshold"
          hint="Cipher alerts when daily spend crosses this."
          endpoint={ENDPOINTS.costAlertThreshold}
          field="threshold"
          unit="USD"
          min={0}
        />
        <NumberSetting
          label="Claude API hard cap"
          hint="The circuit breaker trips and stops spending at this figure."
          endpoint={ENDPOINTS.circuitBreakerHardCap}
          field="hard_cap"
          unit="USD"
          min={0}
        />
      </Block>

      <Block title="Circuit breaker" note="Current state of the spending cap.">
        <Gate resource={breaker}>
          {(d) => (
            <>
              <Row label="Tripped">
                <StatusDot
                  tone={d.tripped ? "red" : "green"}
                  label={d.tripped ? "Tripped — spending halted" : "Not tripped"}
                />
              </Row>
              <Row label="Spend today">
                <Value mono>{d.daily_spend ?? d.spend_today}</Value>
              </Row>
              <Row label="Cap">
                <Value mono>{d.hard_cap ?? d.cap}</Value>
              </Row>
            </>
          )}
        </Gate>
      </Block>

      <Block
        title="API usage"
        note="A1's diagnostics home. Cost accounting sits with infrastructure, not with what the company is doing now."
      >
        <Gate resource={usage} empty="No API usage has been recorded.">
          {(d) => (
            <>
              {Object.entries(d || {})
                .filter(([, v]) => v === null || ["string", "number", "boolean"].includes(typeof v))
                .map(([k, v]) => (
                  <Row key={k} label={k.replace(/_/g, " ")}>
                    <Value mono>{v === null ? null : String(v)}</Value>
                  </Row>
                ))}
            </>
          )}
        </Gate>
      </Block>
    </>
  );
}

/**
 * The parts of §9's Infrastructure clause with nothing behind them.
 *
 * §9 names RunPod/GPU, databases, storage and deployment status. Databases now
 * has a (business-unit-grain) endpoint — see DatabaseHealth above — so it is
 * dropped from this list. The rest still has nothing behind them. Stating
 * that is the honest version of the section; inventing a GPU tile would be
 * the dishonest one (R6).
 */
function NoEndpointYet() {
  const ITEMS = [
    ["RunPod / GPU", "No endpoint reports GPU or RunPod state."],
    ["Storage", "No endpoint reports storage."],
    ["Deployment status", "No endpoint reports what is deployed."],
  ];

  return (
    <Block
      title="Not reported yet"
      note="Named by §9, with no endpoint behind them. Listed rather than left as a gap."
    >
      {ITEMS.map(([label, detail]) => (
        <Row key={label} label={label}>
          <Value>
            <span className="dim">{detail}</span>
          </Value>
        </Row>
      ))}
    </Block>
  );
}

export default function Infrastructure() {
  return (
    <>
      <IntegrationHealth />
      <DatabaseHealth />
      <SchedulerHealth />
      <CostControls />
      <NoEndpointYet />
    </>
  );
}
